import { isMenuCategory } from "@/constants";
import { parseMoneyInputToCents } from "@/lib/money";
import {
  buildItemDescriptionTranslations,
  buildRequiredTranslations,
  findMissingTranslations,
  hasPartialTranslations,
  type TranslationFormValues,
} from "@/lib/translationResolution";
import type { ActivityItemCreate, MenuCategory } from "@/types";

export type ProductItemFormError =
  | "name"
  | "descriptionPartial"
  | "undiscountedPrice"
  | "discountedPrice"
  | "discountedMustBeLower"
  | "price";

export interface ProductItemFormValues {
  itemType: "menu" | "service";
  /** A menu category value, a service category id, or `""` for none. */
  categoryValue: string;
  nameTranslations: TranslationFormValues;
  descriptionTranslations: TranslationFormValues;
  price: string;
  showKioskDiscount: boolean;
  undiscountedPrice: string;
  discountedPrice: string;
  /** Undiscounted price stored on the item being edited; `null` when creating or unset. */
  savedUndiscountedPrice: number | null;
}

/** Fields shared by the create and update requests. */
export type ProductItemBody = Omit<ActivityItemCreate, "commission_rate">;

export type ProductItemBodyResult =
  | { ok: true; body: ProductItemBody }
  | { ok: false; error: ProductItemFormError };

type CategoryPayload =
  | { menu_category: MenuCategory | null; service_category_id: string | null }
  | { service_category_id: string | null };

function categoryPayload(
  itemType: ProductItemFormValues["itemType"],
  categoryValue: string,
): CategoryPayload {
  if (itemType === "service") {
    return { service_category_id: categoryValue || null };
  }
  if (isMenuCategory(categoryValue)) {
    return { menu_category: categoryValue, service_category_id: null };
  }
  return { menu_category: null, service_category_id: categoryValue || null };
}

type PricePayload =
  | { ok: true; prices: Pick<ProductItemBody, "price" | "undiscounted_price"> }
  | { ok: false; error: ProductItemFormError };

/**
 * With the kiosk discount on, `price` is the discounted price and
 * `undiscounted_price` the struck-through one; turning it off on an item that
 * had one clears it explicitly.
 */
function pricePayload(values: ProductItemFormValues): PricePayload {
  if (values.showKioskDiscount) {
    const undiscounted = parseMoneyInputToCents(values.undiscountedPrice);
    if (undiscounted == null) return { ok: false, error: "undiscountedPrice" };
    const discounted = parseMoneyInputToCents(values.discountedPrice);
    if (discounted == null) return { ok: false, error: "discountedPrice" };
    if (discounted >= undiscounted) {
      return { ok: false, error: "discountedMustBeLower" };
    }
    return {
      ok: true,
      prices: { price: discounted, undiscounted_price: undiscounted },
    };
  }
  const price = parseMoneyInputToCents(values.price);
  if (price == null) return { ok: false, error: "price" };
  return {
    ok: true,
    prices:
      values.savedUndiscountedPrice != null
        ? { price, undiscounted_price: null }
        : { price },
  };
}

/** Validates the form and builds the item request body, or names the first invalid field. */
export function buildProductItemBody(
  values: ProductItemFormValues,
): ProductItemBodyResult {
  if (findMissingTranslations(values.nameTranslations).length > 0) {
    return { ok: false, error: "name" };
  }
  if (hasPartialTranslations(values.descriptionTranslations)) {
    return { ok: false, error: "descriptionPartial" };
  }
  const pricing = pricePayload(values);
  if (!pricing.ok) return pricing;
  return {
    ok: true,
    body: {
      name_translations: buildRequiredTranslations(values.nameTranslations),
      description_translations: buildItemDescriptionTranslations(
        values.descriptionTranslations,
      ),
      ...pricing.prices,
      ...categoryPayload(values.itemType, values.categoryValue),
    },
  };
}
